import React, { useState } from 'react';
import { Mail, Server, Key, RefreshCw, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const AnchorageEmailSettings = ({ formData, handleChange, user }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Admin kontrolü
  if (user?.role !== 'admin') {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: '2rem' }}>
          <AlertCircle size={48} color="#f59e0b" style={{ marginBottom: '1rem' }} />
          <h3 style={{ color: '#92400e' }}>Yetki Gerekli</h3>
          <p style={{ color: '#a16207' }}>Mail ayarlarını sadece admin kullanıcılar düzenleyebilir.</p>
        </div>
      </div>
    );
  }

  const testConnection = async () => {
    setTesting(true);
    try {
      // Test connection by triggering sync
      const response = await api.post('/anchorage/sync-emails');
      if (response.data) {
        toast.success(`Bağlantı başarılı! ${response.data.new_entries || 0} yeni kayıt bulundu.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bağlantı testi başarısız');
    } finally {
      setTesting(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/anchorage/sync-emails');
      toast.success(`Senkronizasyon tamamlandı! ${response.data.new_entries || 0} yeni giriş, ${response.data.updated_exits || 0} çıkış güncellendi.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Senkronizasyon başarısız');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Mail size={24} color="#0ea5e9" />
          Demirsahası Mail Ayarları
        </h3>
        <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
          Demirsahasındaki gemi giriş/çıkış bilgilerini çekmek için mail sunucusu ayarlarını yapılandırın.
        </p>
        
        {/* IMAP Server Settings */}
        <div style={{ 
          background: '#f8fafc', 
          padding: '1.25rem', 
          borderRadius: '8px', 
          marginBottom: '1.5rem',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Server size={18} color="#475569" />
            IMAP Sunucu Ayarları
          </h4>
          
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">IMAP Sunucu</label>
              <input
                type="text"
                className="form-input"
                value={formData.anchorage_imap_server || ''}
                onChange={(e) => handleChange('anchorage_imap_server', e.target.value)}
                placeholder="mail.example.com"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Port</label>
              <input
                type="number"
                className="form-input"
                value={formData.anchorage_imap_port || 993}
                onChange={(e) => handleChange('anchorage_imap_port', parseInt(e.target.value) || 993)}
                placeholder="993"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">E-posta Adresi</label>
              <input
                type="email"
                className="form-input"
                value={formData.anchorage_imap_email || ''}
                onChange={(e) => handleChange('anchorage_imap_email', e.target.value)}
                placeholder="dataship@example.com"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Şifre</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  value={formData.anchorage_imap_password || ''}
                  onChange={(e) => handleChange('anchorage_imap_password', e.target.value)}
                  placeholder="••••••••"
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* SMTP Server Settings - Giden Mail */}
        <div style={{ 
          background: '#fef3c7', 
          padding: '1.25rem', 
          borderRadius: '8px', 
          marginBottom: '1.5rem',
          border: '1px solid #fcd34d'
        }}>
          <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Server size={18} color="#d97706" />
            SMTP Sunucu Ayarları (Giden Mail - Bildirimler İçin)
          </h4>
          <p style={{ color: '#92400e', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Şüpheli giriş uyarıları ve diğer bildirimler için SMTP ayarlarını yapılandırın.
          </p>
          
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">SMTP Sunucu</label>
              <input
                type="text"
                className="form-input"
                value={formData.anchorage_smtp_server || ''}
                onChange={(e) => handleChange('anchorage_smtp_server', e.target.value)}
                placeholder="smtp.example.com"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Port (465=SSL, 587=TLS)</label>
              <input
                type="number"
                className="form-input"
                value={formData.anchorage_smtp_port || 465}
                onChange={(e) => handleChange('anchorage_smtp_port', parseInt(e.target.value) || 465)}
                placeholder="465"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">SMTP Kullanıcı Adı</label>
              <input
                type="text"
                className="form-input"
                value={formData.anchorage_smtp_user || ''}
                onChange={(e) => handleChange('anchorage_smtp_user', e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div className="form-group">
              <label className="form-label">SMTP Şifre</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  value={formData.anchorage_smtp_password || ''}
                  onChange={(e) => handleChange('anchorage_smtp_password', e.target.value)}
                  placeholder="••••••••"
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">Gönderen E-posta Adresi</label>
            <input
              type="email"
              className="form-input"
              value={formData.anchorage_sender_email || ''}
              onChange={(e) => handleChange('anchorage_sender_email', e.target.value)}
              placeholder="noreply@example.com"
            />
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Bildirim mailleri bu adresten gönderilecek
            </small>
          </div>
        </div>
        
        {/* Filter Settings */}
        <div style={{ 
          background: '#f0fdf4', 
          padding: '1.25rem', 
          borderRadius: '8px', 
          marginBottom: '1.5rem',
          border: '1px solid #bbf7d0'
        }}>
          <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Key size={18} color="#16a34a" />
            Filtre Ayarları
          </h4>
          
          <div className="form-group">
            <label className="form-label">Gönderen Filtresi</label>
            <input
              type="text"
              className="form-input"
              value={formData.anchorage_sender_filter || ''}
              onChange={(e) => handleChange('anchorage_sender_filter', e.target.value)}
              placeholder="alerts@shippingexplorer.net"
            />
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Sadece bu adresten gelen mailler işlenecektir.
            </small>
          </div>
          
          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.anchorage_auto_sync !== false}
                onChange={(e) => handleChange('anchorage_auto_sync', e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: '#16a34a' }}
              />
              <span style={{ fontWeight: 500 }}>Otomatik Senkronizasyon</span>
            </label>
            <small style={{ color: '#64748b', fontSize: '0.75rem', marginLeft: '26px', display: 'block' }}>
              Kullanıcı login olduğunda otomatik olarak mail kontrolü yapılır.
            </small>
          </div>
        </div>
        
        {/* Actions */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={testConnection}
            disabled={testing}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            {testing ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Test Ediliyor...
              </>
            ) : (
              <>
                <Check size={16} />
                Bağlantı Testi
              </>
            )}
          </button>
          
          <button
            type="button"
            onClick={syncNow}
            disabled={syncing}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            {syncing ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Senkronize Ediliyor...
              </>
            ) : (
              <>
                <RefreshCw size={16} />
                Şimdi Senkronize Et
              </>
            )}
          </button>
        </div>
        
        {/* Info Note */}
        <div style={{ 
          marginTop: '1.5rem', 
          padding: '0.75rem 1rem', 
          background: '#eff6ff', 
          borderRadius: '6px',
          border: '1px solid #bfdbfe',
          fontSize: '0.85rem',
          color: '#1e40af'
        }}>
          <strong>Bilgi:</strong> Shipping Explorer veya benzeri servislerden gelen gemi giriş/çıkış
          uyarı mailleri otomatik olarak işlenir ve demirsahası takip sistemine eklenir.
        </div>
      </div>
    </div>
  );
};

export default AnchorageEmailSettings;
